import {
  Container,
  Box,
  Flex,
  Text,
  VStack,
  Icon,
  HStack,
  Switch,
  Button,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Settings, Sparkles, Moon, Sun, RefreshCw } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { Copyright, BuyCoffeeButton, SelectAddressForm } from "../../components";
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";
import { useThemeMode } from "../../context/ThemeProvider";
import { scheduleKey, FORCE_SCHEDULE_REFRESH_REQUESTED_KEY } from "../../helpers/config";

const HeaderContainer = styled.header<{ $theme: AppTheme }>`
  background: ${({ $theme }) => $theme.gradientHeader};
  padding: 3.5rem 1rem;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: ${({ $theme }) => `${$theme.primary}20`};
    filter: blur(60px);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -15%;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: ${({ $theme }) => `${$theme.accent}15`};
    filter: blur(50px);
  }
`;

const MotionBox = motion(Box);

const SettingItem = styled(Box)<{ $theme: AppTheme }>`
  padding: 1.5rem;
  border-radius: ${THEME.borderRadiusLg};
  background: ${({ $theme }) => $theme.backgroundMuted};
  border: 1px solid ${({ $theme }) => $theme.border};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${({ $theme }) => $theme.primary}40;
  }
`;

export function SettingsPage() {
  const theme = useTheme() as AppTheme;
  const { themeMode, toggleTheme } = useThemeMode();
  const isDark = themeMode === "dark";
  const navigate = useNavigate();
  const toast = useToast();

  const handleForceRefreshSchedule = () => {
    localStorage.removeItem(scheduleKey);
    localStorage.setItem(FORCE_SCHEDULE_REFRESH_REQUESTED_KEY, "1");
    toast({
      title: "Odświeżanie harmonogramu",
      description: "Ponowne pobieranie harmonogramu z serwera...",
      status: "info",
      duration: 3000,
      isClosable: true,
    });
    navigate("/");
  };
  
  return (
    <Box minH="100vh" bg={theme.background}>
      <HeaderContainer $theme={theme}>
        <Container maxW="container.lg" position="relative" zIndex={1}>
          <MotionBox
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Flex align="center" gap={4} mb={4}>
              <Box
                p={3.5}
                borderRadius={THEME.borderRadiusLg}
                bg="rgba(255,255,255,0.15)"
                backdropFilter="blur(10px)"
                border="1px solid rgba(255,255,255,0.2)"
                boxShadow="0 8px 32px rgba(0,0,0,0.1)"
              >
                <Icon as={Settings} boxSize={7} color="white" />
              </Box>
              <Box flex={1}>
                <Flex gap={2} mb={1} align="center">
                  <Text
                    fontFamily="'Outfit', sans-serif"
                    fontSize="3xl"
                    fontWeight="900"
                    color="white"
                    letterSpacing="-0.03em"
                  >
                    Ustawienia
                  </Text>
                  <Icon as={Sparkles} boxSize={6} color="white" opacity={0.8} />
                </Flex>
                <Text color="whiteAlpha.900" fontSize="md" fontWeight="500">
                  Zarządzaj ustawieniami aplikacji
                </Text>
              </Box>
            </Flex>
          </MotionBox>
        </Container>
      </HeaderContainer>

      <Container maxW="container.lg" py={10} px={4}>
        <VStack spacing={8} align="stretch">
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            bg={theme.backgroundCard}
            borderRadius={THEME.borderRadiusXl}
            p={8}
            border={`1px solid ${theme.borderCard}`}
            boxShadow={theme.shadowMd}
          >
            <SelectAddressForm />
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            bg={theme.backgroundCard}
            borderRadius={THEME.borderRadiusXl}
            p={6}
            border={`1px solid ${theme.borderCard}`}
            boxShadow={theme.shadowMd}
          >
            <VStack spacing={4} align="stretch">
              <Text
                fontFamily="'Outfit', sans-serif"
                fontSize="lg"
                fontWeight={700}
                color={theme.text}
                mb={2}
              >
                Wygląd
              </Text>
              
              <SettingItem $theme={theme}>
                <HStack justify="space-between" align="center">
                  <HStack spacing={3} flex={1}>
                    <Box
                      p={2}
                      borderRadius={THEME.borderRadius}
                      bg={`${theme.primary}15`}
                      color={theme.primary}
                    >
                      <Icon as={isDark ? Moon : Sun} boxSize={5} />
                    </Box>
                    <Box>
                      <Text
                        fontFamily="'Outfit', sans-serif"
                        fontSize="md"
                        fontWeight={600}
                        color={theme.text}
                        mb={1}
                      >
                        Tryb ciemny
                      </Text>
                      <Text
                        fontSize="sm"
                        color={theme.textMuted}
                        fontWeight={500}
                      >
                        {isDark ? "Włączony" : "Wyłączony"}
                      </Text>
                    </Box>
                  </HStack>
                  <Switch
                    size="lg"
                    colorScheme="green"
                    isChecked={isDark}
                    onChange={() => toggleTheme()}
                    sx={{
                      "span[data-checked]": {
                        backgroundColor: `${theme.primary} !important`,
                      },
                    }}
                  />
                </HStack>
              </SettingItem>
            </VStack>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            bg={theme.backgroundCard}
            borderRadius={THEME.borderRadiusXl}
            p={6}
            border={`1px solid ${theme.borderCard}`}
            boxShadow={theme.shadowMd}
          >
            <VStack spacing={4} align="stretch">
              <Text
                fontFamily="'Outfit', sans-serif"
                fontSize="lg"
                fontWeight={700}
                color={theme.text}
                mb={2}
              >
                Harmonogram
              </Text>
              <SettingItem $theme={theme}>
                <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
                  <Box>
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="md"
                      fontWeight={600}
                      color={theme.text}
                      mb={1}
                    >
                      Wymuś odświeżenie danych
                    </Text>
                    <Text fontSize="sm" color={theme.textMuted} fontWeight={500}>
                      Pobierz harmonogram ponownie z serwera (przydatne po zmianach po stronie dostawcy)
                    </Text>
                  </Box>
                  <Button
                    leftIcon={<Icon as={RefreshCw} boxSize={4} />}
                    onClick={handleForceRefreshSchedule}
                    colorScheme="green"
                    variant="outline"
                    borderColor={theme.primary}
                    color={theme.primary}
                    _hover={{ bg: `${theme.primary}15` }}
                    size="md"
                  >
                    Odśwież harmonogram
                  </Button>
                </HStack>
              </SettingItem>
            </VStack>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            bg={theme.backgroundCard}
            borderRadius={THEME.borderRadiusXl}
            p={6}
            border={`1px solid ${theme.borderCard}`}
            boxShadow={theme.shadowMd}
          >
            <Flex justify="center">
              <BuyCoffeeButton />
            </Flex>
          </MotionBox>
        </VStack>

        <Box mt={12} mb={6}>
          <Copyright />
        </Box>
      </Container>
    </Box>
  );
}

